// vue
import { ref } from '@vue/composition-api';
import { useStore } from '@/store';

// babylonjs
import * as BABYLONMATERIALS from 'babylonjs-materials';
import 'babylonjs-loaders';

// canvg
import Canvg from 'canvg';

export const useBabylonEngine = (canvasElement: HTMLCanvasElement) => {
    // hooks
    const { apiUrl } = useStore().state;

    // babylon
    const canvas = ref<HTMLCanvasElement>(canvasElement);
    const engine = ref<BABYLON.Engine>();
    const scene = ref<BABYLON.Scene>();
    const camera = ref<BABYLON.ArcRotateCamera>();
    const pointLight = ref<BABYLON.PointLight>();

    // gltf model
    const modelPosition = ref<BABYLON.Vector3 | null>();

    // movement
    const smoothingFactor = 4;
    const startingPoint = ref<BABYLON.Vector3 | null>();
    const targetPoint = ref<BABYLON.Vector3>(new BABYLON.Vector3(0, 0, 0));

    // state
    const isLoading = ref(true);

    // clock
    const fps = ref(0);
    const rateLimitPan = ref(Date.now());
    const time = ref(0);

    // callbacks
    const onEpoch = () => {};
    const onInit = () => {};

    /**
     * Creates a new babylon engine using the canvas set in the constructor,
     * then calls all initialisation methods followed by starting the main
     * babylon engine loop.
     */
    const init = async () => {
        engine.value = new BABYLON.Engine(canvas.value, true);
        initScene();
        initCamera();
        initLights();
        initSkybox();

        if (onInit) {
            await onInit();
        }

        engine.value.runRenderLoop(
            function (this) {
                if (scene.value) {
                    scene.value.render();
                }
                if (engine.value) {
                    fps.value = engine.value.getFps();
                }
                updateTime();
            }.bind(this),
        );

        window.addEventListener('resize', () => {
            if (engine.value) {
                engine.value.resize();
            }
        });
    };

    /**
     * Destroys the current babylon instance.
     */
    const destroy = () => {
        isLoading.value = true;
        if (scene.value) {
            scene.value.dispose();
        }
        if (engine.value) {
            engine.value.dispose();
        }
    };

    /**
     * Initialises the babylon scene property.
     */
    const initScene = () => {
        if (engine.value) {
            scene.value = new BABYLON.Scene(engine.value, {
                useGeometryUniqueIdsMap: true,
                useMaterialMeshMap: true,
                useClonedMeshMap: true,
            });
        }
    };

    /**
     * Initialises an ArcRotateCamera and pointer events on the course mesh to
     * handle placing models onto the course.
     */
    const initCamera = () => {
        if (scene.value) {
            camera.value = new BABYLON.ArcRotateCamera(
                'ArcRotateCamera',
                0,
                1,
                100,
                new BABYLON.Vector3(0, 0, 0),
                scene.value,
            );
        }

        if (camera.value) {
            camera.value.speed = 50;
            camera.value.wheelDeltaPercentage = 0.05;
            camera.value.panningSensibility = 0;
            camera.value.lowerRadiusLimit = 0.1;
            camera.value.upperRadiusLimit = 50;
            camera.value.alpha = -Math.PI / 2;
            camera.value.beta = Math.PI / 8;
            // camera.value.maxZ = 500;

            if (canvas.value) {
                camera.value.attachControl(canvas.value, true);
            }
        }

        const getCanvMeshPosition = () => {
            let pickInfo;
            if (scene.value) {
                pickInfo = scene.value.pick(
                    scene.value.pointerX,
                    scene.value.pointerY,
                );
            }
            if (pickInfo && pickInfo.hit) {
                return pickInfo.pickedPoint;
            }

            return null;
        };

        const onPointerDown = (evt) => {
            if (evt.button === 0) {
                rateLimitPan.value = Date.now();
                const pickInfo = getCanvMeshPosition();
                if (pickInfo) {
                    startingPoint.value = pickInfo;
                    targetPoint.value = startingPoint.value as BABYLON.Vector3;
                    if (startingPoint.value) {
                        startingPoint.value.y = 0;
                    }

                    if (startingPoint.value && camera.value) {
                        camera.value.detachControl(canvas.value);
                    }
                }
                return;
            }

            startingPoint.value = null;
            return;
        };

        const onPointerUp = () => {
            if (startingPoint.value && camera.value) {
                camera.value.attachControl(canvas.value, true);
                startingPoint.value = null;
                return;
            }
        };

        const onPointerMove = (evt) => {
            if (!startingPoint.value) {
                return;
            }
            if (rateLimitPan.value < Date.now() - 50) {
                rateLimitPan.value = Date.now();

                let current = getCanvMeshPosition();
                if (!current) {
                    return;
                }
                current.y = 0;

                targetPoint.value = current;
            }
        };

        canvas.value.addEventListener('pointerdown', onPointerDown, false);
        canvas.value.addEventListener('pointerup', onPointerUp, false);
        canvas.value.addEventListener('pointermove', onPointerMove, false);

        if (scene.value) {
            scene.value.onDispose = () => {
                canvas.value.removeEventListener('pointerdown', onPointerDown);
                canvas.value.removeEventListener('pointerup', onPointerUp);
                canvas.value.removeEventListener('pointermove', onPointerMove);
            };
        }
    };

    /**
     * Initialises a point light for on the scene.
     */
    const initLights = () => {
        if (scene.value) {
            pointLight.value = new BABYLON.PointLight(
                'pointLight',
                new BABYLON.Vector3(500, 500, 500),
                scene.value,
            );
        }
        if (pointLight.value) {
            pointLight.value.diffuse = new BABYLON.Color3(1, 1, 1);
            pointLight.value.specular = new BABYLON.Color3(1, 1, 1);
        }
    };

    /**
     * Initialises a skybox retrieved from a remote API.
     */
    const initSkybox = () => {
        const skybox = BABYLON.Mesh.CreateBox('skyBox', 10000.0, scene.value);
        if (scene.value) {
            const skyboxMaterial = new BABYLON.StandardMaterial(
                'skyBox',
                scene.value,
            );
            skyboxMaterial.backFaceCulling = false;
            skyboxMaterial.disableLighting = true;
            skybox.material = skyboxMaterial;
            skybox.infiniteDistance = true;
            skybox.isPickable = false;
            skyboxMaterial.reflectionTexture = new BABYLON.CubeTexture(
                `${apiUrl}/babylon/skybox/clouds`,
                scene.value,
            );
            skyboxMaterial.reflectionTexture.coordinatesMode =
                BABYLON.Texture.SKYBOX_MODE;
        }
    };

    /**
     * Method which gets called each iteration of the main engine loop.
     */
    const updateTime = () => {
        if (onEpoch) {
            onEpoch();
        }
        time.value += 0.02;
    };

    /**
     * Creates a new empty DyanmicTexture with a resolution that is a power of
     * 2.
     *
     * @param {number} [resolutionExponent=13] - The exponent to use in
     * Math.pow(2, resolutionExponent) for calculating texture resolution.
     */
    const generateTexture = (
        resolutionExponent: number = 13,
    ): BABYLON.DynamicTexture => {
        if (!resolutionExponent) {
            resolutionExponent = 13;
        }

        const textureResolution = Math.pow(2, resolutionExponent);
        return new BABYLON.DynamicTexture(
            'dynamicTexture',
            textureResolution,
            scene.value,
            false,
        );
    };

    const drawSvgOnTexture = async (svg: any, texture: any) => {
        let v = await Canvg.from(texture.getContext(), svg);
        v.start();
        texture.update();
    };

    return {
        canvas,
        engine,
        scene,
        camera,
        init,
        generateTexture,
        drawSvgOnTexture,
    };
};
